import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ─── Data ─────────────────────────────────────────────────────────────────────

const INCLUDED = [
  {
    title: 'Your own MST portal',
    desc: 'A fully configured version of the same portal MST runs every day — bookings, itineraries, suppliers, invoices and comms in one place.',
    icon: 'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z',
  },
  {
    title: 'Proven processes & systems',
    desc: 'Step-by-step operating playbooks built over a decade. From first enquiry to post-tour follow-up, every stage is documented.',
    icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z',
  },
  {
    title: 'Monthly expert calls',
    desc: "Monthly sessions with MST's leadership — we're in your corner the whole way.",
    icon: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  },
  {
    title: 'Build your supplier network',
    desc: "MST will help you build your supplier network — whether that's guidance alone or joining stakeholder calls when required.",
    icon: 'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
  },
  {
    title: 'Business development guidance',
    desc: 'Pricing strategy, sales technique and marketing advice from people who built a sports tour business from the ground up.',
    icon: 'M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941',
  },
  {
    title: 'Brand & territory support',
    desc: 'Operate as an authorised MST partner — with brand backing and exclusive territory rights on the franchise model.',
    icon: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418',
  },
];

const PRICING = {
  franchise: {
    setup: '£2,500',
    monthly: '£1,000',
    checklist: [
      'Your own MST portal instance',
      'Full systems & process documentation',
      '10 hours of expert calls per month',
      'Guidance on recruiting your team',
      'Business development guidance',
      'Exclusive territory rights',
    ],
  },
  whitelabel: {
    setup: '£1,500',
    monthly: '£500',
    checklist: [
      'Your own MST portal instance',
      'Full systems & process documentation',
      '5 hours of expert calls per month',
      'Guidance on recruiting your team',
      'Business development guidance',
      'Flexible commercial terms',
    ],
  },
};

const FAQ_ITEMS = [
  {
    q: 'Do I need experience in sports tourism?',
    a: "No. We partner with both established travel companies and ambitious individuals from scratch. Our systems, training and monthly support calls are designed to get you operating confidently, whatever your starting point.",
  },
  {
    q: "What's the difference between franchise and white-label?",
    a: "On the franchise model you trade as MST with exclusive territory rights and full brand backing. With white-label, you keep your own brand and client relationships while MST powers the tours behind the scenes. We'll help you choose on the discovery call.",
  },
  {
    q: 'How quickly can I start selling tours?',
    a: "Because your portal and playbooks are configured during onboarding, most partners are ready to go to market in a matter of weeks rather than the years it takes to build from scratch.",
  },
  {
    q: 'What does the monthly fee cover?',
    a: "Your portal, ongoing access to MST's systems, and monthly expert support calls with our leadership team — the exact number of hours depends on whether you choose the franchise or white-label model.",
  },
];

// ─── Small helpers ─────────────────────────────────────────────────────────────

function Eyebrow({ children, light = false }) {
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] font-bold eyebrow uppercase mb-5 ${light ? 'text-ba' : 'text-pb'}`}>
      <span className="w-6 h-px bg-ba flex-shrink-0" />
      {children}
    </div>
  );
}

function Tick({ amber = false }) {
  return (
    <svg
      className={`w-4 h-4 flex-shrink-0 mt-0.5 ${amber ? 'text-ba' : 'text-sg'}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function JoinMST() {
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', country: '',
    entityType: 'individual', companyName: '',
    model: 'undecided', background: '', hearAbout: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const applyRef = useRef(null);

  // Anti-spam: page-mount timestamp + honeypot field, enforced in firestore.rules
  const mountedAtRef = useRef(new Date());
  const [honeypot, setHoneypot] = useState('');

  // noindex — keep this page out of search engines
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  // Scroll-reveal via IntersectionObserver
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (honeypot) return; // silently drop — bot filled a field real users never see
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'franchiseApplications'), {
        ...form,
        status: 'pending',
        submittedAt: serverTimestamp(),
        honeypot: '',
        clientStartedAt: mountedAtRef.current,
      });
      setSubmitted(true);
      applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white font-sans">

      {/* ════ HEADER ════ */}
      <header className="fixed top-0 inset-x-0 z-50 bg-navy/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src="/MST Logo White.png" alt="MST Sports Tours" className="h-7 w-auto" />
          <nav className="hidden md:flex items-center gap-8 text-[13.5px] font-medium text-white/70">
            {[['opportunity','Opportunity'],['models','How to partner'],['included',"What's included"],['pricing','Pricing']].map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} className="hover:text-white transition-colors">{label}</button>
            ))}
          </nav>
          <button
            onClick={() => scrollTo('apply')}
            className="bg-ba text-navy font-bold text-[13.5px] px-5 py-2.5 rounded-lg hover:brightness-105 transition-all shadow-amber whitespace-nowrap"
          >
            Apply now
          </button>
        </div>
      </header>

      {/* ════ HERO ════ */}
      <section className="relative bg-navy text-white overflow-hidden pt-16">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="absolute -top-32 -right-40 w-[620px] h-[620px] rounded-full bg-pb/30 blur-[120px] pointer-events-none" />
        <div className="absolute top-40 -left-40 w-[460px] h-[460px] rounded-full bg-ba/10 blur-[120px] pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-28 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 reveal">
            <Eyebrow light>MST Partner Network</Eyebrow>
            <h1 className="display font-extrabold text-[40px] sm:text-[54px] lg:text-[60px] mb-6">
              Run world-class sports tours.<br className="hidden sm:block" />
              <span className="text-white/55">Without building it </span>
              <span className="underline-amber">from scratch.</span>
            </h1>
            <p className="text-white/65 text-lg leading-relaxed max-w-xl mb-9">
              Partner with MST to launch fully managed sports tours in your market — backed by a decade of experience, 400+ delivered tours, and the exact systems we run every day. Bring it to market as an MST franchise, or let us power your tours behind your own brand.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollTo('apply')}
                className="inline-flex items-center gap-2 bg-ba text-navy font-bold text-[15px] px-7 py-4 rounded-xl hover:brightness-105 transition-all shadow-amber"
              >
                Apply to partner
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
              <button
                onClick={() => scrollTo('models')}
                className="inline-flex items-center gap-2 text-white/80 font-semibold text-[15px] px-5 py-4 rounded-xl border border-white/15 hover:bg-white/5 hover:text-white transition-all"
              >
                See how it works
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-tr from-ba/20 to-transparent blur-xl" />
              <div className="relative rounded-[22px] overflow-hidden ring-1 ring-white/15 shadow-card-lg img-ph aspect-[4/5]">
                <img
                  src="https://mstsportstours.com/wp-content/uploads/2025/04/MST-Sports-Tour-Stadium-Tours-scaled.webp"
                  alt="MST sports tour in Spain"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="absolute left-5 bottom-5 right-5">
                  <div className="text-[11px] font-bold eyebrow uppercase text-ba mb-1">Costa Blanca, Spain</div>
                  <div className="text-white font-bold text-lg leading-tight">The home of MST tours — now operating in your market.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ TRUST BAR ════ */}
      <section className="bg-navy-700 text-white border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-9 grid lg:grid-cols-[auto_1fr] gap-x-12 gap-y-7 items-center">
          <div className="flex items-center gap-8 sm:gap-12">
            {[
              { value: '10', sup: 'yrs', label: 'Experience' },
              { value: '400', sup: '+',  label: 'Tours delivered' },
              { value: '3',   sup: '',   label: 'Sports & growing', hide: true },
            ].map(({ value, sup, label, hide }, i) => (
              <div key={label} className={`flex items-center gap-8 sm:gap-12 ${i > 0 ? '' : ''}`}>
                {i > 0 && <div className={`w-px h-10 bg-white/15 ${hide ? 'hidden sm:block' : ''}`} />}
                <div className={hide ? 'hidden sm:block' : ''}>
                  <div className="text-3xl font-extrabold text-ba leading-none">
                    {value}{sup && <span className="text-xl align-top">{sup}</span>}
                  </div>
                  <div className="text-[11px] text-white/55 mt-1.5 font-semibold uppercase tracking-wide">{label}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:justify-self-end w-full lg:w-auto">
            <div className="text-[11px] font-bold eyebrow uppercase text-white/40 mb-3 lg:text-right">Trusted by industry partners</div>
            <div className="flex flex-wrap items-center gap-x-9 gap-y-3">
              <img src="/Your Tour logo.jpeg" alt="Your Tour" className="h-7 w-auto partner-logo" />
              <img src="/NSG logo.jpeg" alt="NSG" className="h-8 w-auto partner-logo" />
              <img src="/LLS logo.png" alt="LLS" className="h-7 w-auto partner-logo" />
            </div>
          </div>
        </div>
      </section>

      {/* ════ OPPORTUNITY ════ */}
      <section id="opportunity" className="bg-white px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl reveal">
            <Eyebrow>The opportunity</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] text-navy mb-6">
              Sports tourism is booming. Building the operation behind it is the hard part.
            </h2>
            <p className="text-[17px] text-slate-500 leading-relaxed">
              Clubs, schools and academies are travelling more than ever — but running tours profitably takes suppliers, systems, and a reputation that takes years to earn. We've already built it. Plug into the MST engine and go to market in months, not years.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {[
              { n: '01', title: 'Proven, not theoretical', desc: 'Every process you inherit has been run hundreds of times — refined on real tours with real teams.' },
              { n: '02', title: 'Ready to trade fast', desc: 'Your own portal and playbooks are configured from day one — so you can focus on selling.' },
              { n: '03', title: 'Backed by real people', desc: "Monthly calls with MST's leadership mean you're never figuring out a difficult booking alone." },
            ].map(({ n, title, desc }) => (
              <div key={n} className="reveal rounded-2xl border border-border p-7 hover:shadow-card transition-shadow">
                <div className="text-[40px] font-extrabold text-navy leading-none mb-3">{n}</div>
                <h3 className="font-bold text-[17px] text-navy mb-2">{title}</h3>
                <p className="text-[15px] text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ TWO WAYS TO PARTNER ════ */}
      <section id="models" className="bg-page px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14 reveal">
            <Eyebrow>Two ways to partner</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] text-navy mb-5">Choose the model that fits your business.</h2>
            <p className="text-[17px] text-slate-500 leading-relaxed">Whether you want to fly the MST flag or keep your own brand front and centre, you get the same operational engine underneath.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="reveal relative rounded-3xl bg-navy text-white p-9 overflow-hidden shadow-card-lg">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-pb/40 blur-[90px]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-[11px] font-bold eyebrow uppercase bg-ba text-navy px-3 py-1 rounded-full mb-6">Most popular</div>
                <h3 className="text-2xl font-extrabold mb-2">MST Franchise</h3>
                <p className="text-white/60 text-[15px] leading-relaxed mb-7">Operate under the MST name with exclusive rights to your territory. Our brand, our reputation, our playbook — your business to grow.</p>
                <ul className="space-y-3.5">
                  {['Exclusive territory rights','Trade as MST — with full brand & marketing support','Backed by a decade of MST reputation from day one'].map(item => (
                    <li key={item} className="flex items-start gap-3 text-[15px] text-white/85">
                      <Tick amber />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="reveal relative rounded-3xl bg-white border border-border p-9 shadow-card">
              <div className="inline-flex items-center text-[11px] font-bold eyebrow uppercase px-3 py-1 rounded-full mb-6" style={{ background: '#EBF2FC', color: '#1A4A8A' }}>Keep your brand</div>
              <h3 className="text-2xl font-extrabold text-navy mb-2">White-Label Partnership</h3>
              <p className="text-slate-500 text-[15px] leading-relaxed mb-7">Keep your own brand front and centre. MST systems and processes will power the tours while you own the client relationship end to end.</p>
              <ul className="space-y-3.5">
                {['Your brand, your clients, your pricing','The MST operations engine running quietly underneath','Flexible commercial terms built around your model'].map(item => (
                  <li key={item} className="flex items-start gap-3 text-[15px] text-slate-600">
                    <Tick />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-center text-[14px] text-slate-400 mt-8 reveal">
            Not sure which is right for you?{' '}
            <button onClick={() => scrollTo('apply')} className="text-pb font-semibold hover:underline">Apply and we'll help you decide.</button>
          </p>
        </div>
      </section>

      {/* ════ WHAT'S INCLUDED ════ */}
      <section id="included" className="bg-white px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14 reveal">
            <Eyebrow>What's included</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] text-navy mb-5">Everything you need to run a professional operation.</h2>
            <p className="text-[17px] text-slate-500 leading-relaxed">From the moment you join, the tools, systems and support are ready and waiting.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {INCLUDED.map(({ title, desc, icon }) => (
              <div key={title} className="reveal bg-page rounded-2xl p-7 border border-transparent hover:border-border hover:bg-white hover:shadow-card transition-all">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: '#EBF2FC' }}>
                  <svg className="w-6 h-6 text-pb" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                  </svg>
                </div>
                <h3 className="font-bold text-navy text-[16px] mb-2">{title}</h3>
                <p className="text-[14.5px] text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ HOW IT WORKS ════ */}
      <section className="bg-navy text-white px-6 py-24 relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14 reveal">
            <Eyebrow light>How it works</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] mb-5">From application to your first tour.</h2>
            <p className="text-[17px] text-white/60 leading-relaxed">A clear, supported path — we move at your pace and only proceed when it's the right fit for both sides.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
            {[
              { n: '01', title: 'Apply', desc: 'Tell us about you and the market you want to serve. Takes a few minutes.' },
              { n: '02', title: 'Discovery call', desc: "We explore fit together, answer your questions and agree the right model." },
              { n: '03', title: 'Onboard', desc: 'Portal setup, systems training, recruitment assistance and your operating playbook.' },
              { n: '04', title: 'Launch', desc: "Start selling and delivering tours with MST's full support behind you." },
            ].map(({ n, title, desc }) => (
              <div key={n} className="reveal bg-navy p-7">
                <div className="text-ba text-[13px] font-bold eyebrow uppercase mb-4">Step {n}</div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-[14.5px] text-white/60 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ PROOF / ABOUT ════ */}
      <section className="bg-page px-6 py-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="rounded-[22px] overflow-hidden img-ph aspect-[5/4] shadow-card-lg ring-1 ring-black/5">
              <img
                src="https://mstsportstours.com/wp-content/uploads/2025/04/MST-NSG-Sports-Tour-scaled.webp"
                alt="MST & NSG sports tour"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <Eyebrow>Built on real experience</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] text-navy mb-6">A decade of tours. A network you can't buy off the shelf.</h2>
            <p className="text-[17px] text-slate-500 leading-relaxed mb-5">
              MST Sports Tours has delivered exceptional, fully managed tours for the better part of a decade. We hold the supplier relationships, the operating systems and the reputation that most operators spend years trying to build.
            </p>
            <p className="text-[17px] text-slate-500 leading-relaxed mb-8">
              Now we're opening that infrastructure to ambitious companies and individuals who want to build a professional sports tour business in their own market — without starting from zero.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-border p-5">
                <div className="font-bold text-navy text-[15px] mb-1">Official European tour provider</div>
                <div className="text-[14px] text-slate-500">Appointed by NSG to deliver their European tours.</div>
              </div>
              <div className="rounded-xl bg-white border border-border p-5">
                <div className="font-bold text-navy text-[15px] mb-1">Official Spain tour provider</div>
                <div className="text-[14px] text-slate-500">Trusted by Your Tour UK for all tours to Spain.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Pull quote — confirm this wording with Michael before launch */}
        <div className="max-w-4xl mx-auto mt-20 text-center reveal">
          <svg className="w-10 h-10 mx-auto mb-5" style={{ color: 'rgba(232,160,32,0.4)' }} fill="currentColor" viewBox="0 0 24 24">
            <path d="M9.5 7C6.5 8 5 10.5 5 14v3h5v-6H7c0-2 .8-3.3 2.5-4L9.5 7zm9 0c-3 1-4.5 3.5-4.5 7v3h5v-6h-3c0-2 .8-3.3 2.5-4L18.5 7z" />
          </svg>
          <p className="display font-extrabold text-[26px] sm:text-[32px] text-navy leading-snug mb-6">
            "We've spent ten years perfecting how a great sports tour is run. The systems and processes already exist so others don't have to learn it the hard way."
          </p>
          <div className="flex items-center justify-center gap-3">
            <img
              src="https://mstsportstours.com/wp-content/uploads/2025/05/Michael-McDonough-Director-MST-Spain.webp"
              alt="Michael McDonough"
              className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow"
              loading="lazy"
            />
            <div className="text-left">
              <div className="font-bold text-navy text-[15px] leading-tight">Michael McDonough</div>
              <div className="text-[13px] text-slate-500">Director, MST Sports Tours</div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ PRICING ════ */}
      <section id="pricing" className="bg-white px-6 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-xl mx-auto mb-12 reveal">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] text-navy mb-4">Simple, transparent, no surprises.</h2>
            <p className="text-[17px] text-slate-500 leading-relaxed">One straightforward investment to get set up, and a flat monthly fee while you grow. No hidden tiers.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 reveal">
            {/* Franchise */}
            <div className="relative rounded-3xl bg-navy text-white p-9 overflow-hidden shadow-card-lg flex flex-col">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-pb/40 blur-[90px]" />
              <div className="relative flex flex-col flex-1">
                <div className="inline-flex items-center gap-2 text-[11px] font-bold eyebrow uppercase bg-ba text-navy px-3 py-1 rounded-full mb-6 self-start">Most popular</div>
                <h3 className="text-xl font-extrabold mb-6">MST Franchise</h3>
                <div className="mb-2">
                  <div className="text-4xl font-extrabold">{PRICING.franchise.setup}</div>
                  <div className="text-white/55 text-[14px] mt-1">one-time setup</div>
                </div>
                <div className="my-5 h-px bg-white/15" />
                <div className="mb-7">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold">{PRICING.franchise.monthly}</span>
                    <span className="text-white/55 font-semibold text-lg">/mo</span>
                  </div>
                  <div className="text-white/55 text-[14px] mt-1">ongoing support & systems</div>
                </div>
                <ul className="space-y-3 mb-8">
                  {PRICING.franchise.checklist.map(item => (
                    <li key={item} className="flex items-start gap-2.5 text-[14.5px] text-white/85">
                      <Tick amber />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => scrollTo('apply')}
                  className="mt-auto block text-center bg-ba text-navy font-bold py-4 rounded-xl hover:brightness-105 transition-all shadow-amber"
                >
                  Apply now
                </button>
              </div>
            </div>

            {/* White-label */}
            <div className="relative rounded-3xl bg-white border border-border p-9 shadow-card flex flex-col">
              <div className="inline-flex items-center text-[11px] font-bold eyebrow uppercase px-3 py-1 rounded-full mb-6 self-start" style={{ background: '#EBF2FC', color: '#1A4A8A' }}>Keep your brand</div>
              <h3 className="text-xl font-extrabold text-navy mb-6">White-Label Partnership</h3>
              <div className="mb-2">
                <div className="text-4xl font-extrabold text-navy">{PRICING.whitelabel.setup}</div>
                <div className="text-slate-400 text-[14px] mt-1">one-time setup</div>
              </div>
              <div className="my-5 h-px bg-border" />
              <div className="mb-7">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-navy">{PRICING.whitelabel.monthly}</span>
                  <span className="text-slate-400 font-semibold text-lg">/mo</span>
                </div>
                <div className="text-slate-400 text-[14px] mt-1">ongoing support & systems</div>
              </div>
              <ul className="space-y-3 mb-8">
                {PRICING.whitelabel.checklist.map(item => (
                  <li key={item} className="flex items-start gap-2.5 text-[14.5px] text-slate-600">
                    <Tick />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollTo('apply')}
                className="mt-auto block text-center bg-navy text-white font-bold py-4 rounded-xl hover:bg-pb transition-all"
              >
                Apply now
              </button>
            </div>
          </div>

          <p className="text-center text-[14px] text-slate-400 mt-8 reveal">
            Indicative pricing — final terms are confirmed on your discovery call.
          </p>
        </div>
      </section>

      {/* ════ FAQ ════ */}
      <section className="bg-page px-6 py-24">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 reveal">
            <Eyebrow>Common questions</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] text-navy">Good to know before you apply.</h2>
          </div>
          <div className="space-y-3">
            {FAQ_ITEMS.map(({ q, a }, i) => (
              <details key={q} className="group bg-white rounded-xl border border-border overflow-hidden" open={i === 0}>
                <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none">
                  <span className="font-bold text-navy text-[16px]">{q}</span>
                  <svg className="w-5 h-5 text-pb flex-shrink-0 transition-transform group-open:rotate-45" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </summary>
                <p className="px-6 pb-5 -mt-1 text-[15px] text-slate-500 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ════ APPLICATION FORM ════ */}
      <section id="apply" ref={applyRef} className="bg-white px-6 py-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 reveal">
            <Eyebrow>Apply to partner</Eyebrow>
            <h2 className="display font-extrabold text-[32px] sm:text-[42px] text-navy mb-4">Start the conversation.</h2>
            <p className="text-[17px] text-slate-500 leading-relaxed">Tell us a little about yourself and we'll be in touch to explore whether MST is the right fit. No commitment.</p>
          </div>

          {submitted ? (
            <div className="text-center rounded-3xl border border-border shadow-card p-12">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-light">
                <svg className="w-8 h-8 text-sg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-extrabold text-navy mb-3">Application received</h3>
              <p className="text-slate-500 leading-relaxed text-[15px] max-w-sm mx-auto">
                Thank you for your interest in joining the MST partner network. We'll review your application and be in touch shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="reveal rounded-3xl border border-border shadow-card p-8 sm:p-9 space-y-5">
              {/* Honeypot — hidden from real visitors, bots that fill every field trip it */}
              <input
                type="text" name="website" tabIndex="-1" autoComplete="off"
                value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                aria-hidden="true"
              />
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[13.5px] font-semibold text-navy mb-1.5">Full name <span className="text-ba">*</span></label>
                  <input type="text" required value={form.fullName} onChange={e => set('fullName', e.target.value)} placeholder="Your full name" className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface" />
                </div>
                <div>
                  <label className="block text-[13.5px] font-semibold text-navy mb-1.5">Email address <span className="text-ba">*</span></label>
                  <input type="email" required value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[13.5px] font-semibold text-navy mb-1.5">Phone number <span className="text-ba">*</span></label>
                  <input type="tel" required value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+44 7700 000000" className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface" />
                </div>
                <div>
                  <label className="block text-[13.5px] font-semibold text-navy mb-1.5">Location <span className="text-ba">*</span></label>
                  <input type="text" required value={form.country} onChange={e => set('country', e.target.value)} placeholder="Where you want to operate" className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface" />
                </div>
              </div>

              <div>
                <label className="block text-[13.5px] font-semibold text-navy mb-2">
                  Are you applying as an individual or a company? <span className="text-ba">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {['individual', 'company'].map(opt => (
                    <label key={opt} className="cursor-pointer">
                      <input type="radio" name="entityType" value={opt} checked={form.entityType === opt} onChange={() => set('entityType', opt)} className="sr-only" />
                      <div
                        className={`rounded-lg border px-4 py-3 text-[15px] font-medium transition-all ${form.entityType === opt ? 'border-pb text-pb' : 'border-border text-slate-600'}`}
                        style={form.entityType === opt ? { background: '#EBF2FC' } : {}}
                      >
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {form.entityType === 'company' && (
                <div>
                  <label className="block text-[13.5px] font-semibold text-navy mb-1.5">Company name <span className="text-ba">*</span></label>
                  <input type="text" required value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Your company name" className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface" />
                </div>
              )}

              <div>
                <label className="block text-[13.5px] font-semibold text-navy mb-1.5">Preferred partnership model</label>
                <select value={form.model} onChange={e => set('model', e.target.value)} className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface text-slate-600">
                  <option value="undecided">Not sure yet — help me decide</option>
                  <option value="franchise">MST Franchise</option>
                  <option value="whitelabel">White-label partnership</option>
                </select>
              </div>

              <div>
                <label className="block text-[13.5px] font-semibold text-navy mb-1.5">
                  Tell us about your background and why you're interested <span className="text-ba">*</span>
                </label>
                <textarea required rows={5} value={form.background} onChange={e => set('background', e.target.value)} placeholder="Your experience in sport, tourism or business, and what you're hoping to build..." className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface resize-none" />
              </div>

              <div>
                <label className="block text-[13.5px] font-semibold text-navy mb-1.5">
                  How did you hear about this opportunity?{' '}
                  <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <input type="text" value={form.hearAbout} onChange={e => set('hearAbout', e.target.value)} placeholder="Word of mouth, social media, etc." className="field w-full border border-border rounded-lg px-4 py-3 text-[15px] bg-surface" />
              </div>

              {error && <p className="text-sm text-dr bg-red-light rounded-lg px-4 py-3">{error}</p>}

              <button type="submit" disabled={submitting} className="w-full bg-navy text-white font-bold py-4 rounded-xl hover:bg-pb transition-colors disabled:opacity-60 text-[15px]">
                {submitting ? 'Submitting...' : 'Submit application'}
              </button>
              <p className="text-[12.5px] text-slate-400 text-center">
                By submitting this form you agree to be contacted by MST Sports Tours regarding your application.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ════ FOOTER ════ */}
      <footer className="bg-navy text-white px-6 py-14">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <img src="/MST Logo White.png" alt="MST Sports Tours" className="h-7 w-auto mb-4 opacity-90" />
            <p className="text-white/45 text-[14px] max-w-md leading-relaxed">
              The sports tour specialists — based in Costa Blanca, Spain. Now opening the MST operation to partners worldwide.
            </p>
          </div>
          <div className="text-[14px] text-white/60 sm:text-right space-y-1.5">
            <a href="mailto:info@mstsportstours.com" className="block hover:text-white transition-colors">info@mstsportstours.com</a>
            <a href="tel:+34663265710" className="block hover:text-white transition-colors">+34 663 265 710</a>
            <div className="text-white/35">Alicante, Spain</div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-white/10 text-white/30 text-[12.5px]">
          © {new Date().getFullYear()} MST Sports Tours Ltd. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
